import { useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Avatar } from "@/components/common/Avatar";
import { BackButton } from "@/components/common/BackButton";
import { Tooltip } from "@/components/common/Tooltip";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Disclosure } from "@/components/design/Disclosure";
import { FilterChip } from "@/components/design/FilterChip";
import { KpiBadge } from "@/components/design/KpiBadge";
import { PowerTile } from "@/components/design/PowerTile";
import { MenuItem } from "@/components/design/MenuItem";
import { AddForm } from "@/components/design/AddForm";
import { AddPlaceholder } from "@/components/design/AddPlaceholder";
import { NumberStepper } from "@/components/design/NumberStepper";
import { Pager } from "@/components/design/Pager";
import { PlusButton } from "@/components/design/PlusButton";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { SecretInput } from "@/components/design/SecretInput";
import { TextArea, TextInput } from "@/components/design/TextField";
import { Select } from "@/components/design/Select";
import { SearchableSelect } from "@/components/design/SearchableSelect";
import { Switch } from "@/components/design/Switch";
import { TriStateControl } from "@/components/design/TriStateControl";
import { Hero } from "@/components/layout/Hero";
import { HorizontalCardCarousel } from "@/components/design/HorizontalCardCarousel";
import { ResultCard } from "@/components/search/ResultCard";
import { SavePersonCollectionCard } from "@/components/save/SaveCollectionTemplate";
import { ShareItemCard, ShareItemCardCover, ShareItemCardFeed } from "@/components/share/ShareItemCard";
import type { ShareItemLike } from "@/lib/share-item";
import { ButtonsTab } from "./styleguide/ButtonsTab";

// Settings · Styleguide — a living overview of the shared UI primitives
// (tokens, buttons, pills, boxes, hero, …). Renders the real classes/components
// so it doubles as a visual contract: if a primitive changes in index.css or a
// component, this page shows it. Dev-facing reference, copy belongs to the
// snapshot of conventions in docs/UI_CONVENTIONS.md. Chrome is localized via
// react-intl; technical specimen identifiers (class names, code snippets,
// glyphs) are carried verbatim and marked `// i18n-allow`.

// One specimen: the live element plus the class/name to copy. Optional
// `purpose` adds a short "when to use this" line — useful on Button
// variants where the visual alone doesn't tell you which one to pick.
function Specimen({ code, purpose, children }: { code: string; purpose?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-[44px] flex-wrap items-center gap-3">{children}</div>
      <code className="font-mono text-[10px] leading-none text-ink/45">{code}</code>
      {purpose ? <p className="text-[11px] leading-relaxed text-ink/55">{purpose}</p> : null}
    </div>
  );
}

// Fixture SharedItems for the card taxonomy below. The inline SVG cover keeps
// the styleguide backend-free; btoa is safe for the ASCII-only markup.
const FIXTURE_COVER = btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300">' +
    '<rect width="400" height="300" fill="#d8c9ae"/>' +
    '<circle cx="320" cy="70" r="90" fill="#c2ad8a"/>' +
    '<rect y="220" width="400" height="80" fill="#b09a75"/></svg>',
);

const FIXTURE_ITEM: ShareItemLike = {
  id: "styleguide-item",
  itemType: "PROJECT",
  name: "Lichtinstallation in der Kokerei",
  shortDescription: "Für eine raumgreifende Lichtinstallation suchen wir Verstärkung im Team.",
  mainImage: { mimeType: "image/svg+xml", content: FIXTURE_COVER },
  rating: 4.6,
  ratings: 12,
  views: 1240,
  comments: 8,
  signUps: 5,
  owner: true,
  pinned: true,
  createdBy: { name: { given: "Greta", family: "Nowak" } },
};

const FIXTURE_ITEM_NO_IMAGE: ShareItemLike = {
  ...FIXTURE_ITEM,
  id: "styleguide-item-2",
  itemType: "POST",
  name: "Post ohne Bild",
  shortDescription: "Ohne mainImage fällt die Karte auf das AbstractTile-Muster zurück.",
  mainImage: null,
  owner: false,
  pinned: false,
};

function PagerSpecimen() {
  const [page, setPage] = useState(0);
  return (
    <div className="w-full max-w-sm">
      <Pager page={page} totalPages={4} onChange={setPage} />
    </div>
  );
}

function GuideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="box bg-surface p-6">
      <BoxHeader title={title} />
      <div className="mt-4 flex flex-col gap-5">{children}</div>
    </div>
  );
}

function Swatch({ cls, label }: { cls: string; label: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className={`h-12 w-full rounded-md ring-1 ring-ink/10 ${cls}`} />
      <code className="font-mono text-[10px] leading-tight text-ink/45">{label}</code>
    </div>
  );
}

function RingSpecimen({ cls, label }: { cls: string; label: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className={`h-12 w-full rounded-md bg-page ring-1 ${cls}`} />
      <code className="font-mono text-[10px] leading-tight text-ink/45">{label}</code>
    </div>
  );
}

export function StyleguidePanel() {
  // The main "Übersicht" tab is the broad overview — colors, tokens, every
  // primitive, cards, hero. Sub-tabs are focused topic deep-dives. Today only
  // Buttons; future could add Text / Farben / Effekte / Layout / ….
  const [tab, setTab] = useState<"overview" | "buttons">("overview");

  const intl = useIntl();

  return (
    <>
      <div className="box bg-surface p-6">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">UI · Styleguide{/* i18n-allow */}</div>
        <div className="mt-1 text-[12px] text-ink/55">
          {tab === "overview" ? (
            <FormattedMessage
              id="settings.styleguide.intro.overview"
              defaultMessage="A living overview of every shared UI primitive – colours, tokens, buttons, cards, hero. Source of truth: index.css + docs/UI_CONVENTIONS.md."
            />
          ) : (
            <FormattedMessage
              id="settings.styleguide.intro.buttons"
              defaultMessage="Buttons in detail – every action surface of the app with production call sites. A specific snapshot that complements the overview."
            />
          )}
        </div>
        <div className="mt-4">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            options={[
              { value: "overview", label: intl.formatMessage({ id: "settings.styleguide.tab.overview", defaultMessage: "Overview" }) },
              { value: "buttons", label: intl.formatMessage({ id: "settings.styleguide.tab.buttons", defaultMessage: "Buttons" }) },
            ]}
          />
        </div>
      </div>

      {tab === "buttons" ? <ButtonsTab /> : <OverviewTabContent />}
    </>
  );
}

function OverviewTabContent() {
  const intl = useIntl();
  const [toggleOn, setToggleOn] = useState(true);
  const [pill, setPill] = useState<"personen" | "posts" | "projekte">("personen");
  const [kpiTab, setKpiTab] = useState<"score" | "must" | "kann">("score");
  const [tri, setTri] = useState<boolean | null>(null);
  const [switchBare, setSwitchBare] = useState(false);
  const [switchLabeled, setSwitchLabeled] = useState(true);
  const [stepperValue, setStepperValue] = useState(8);
  const [secret, setSecret] = useState("");
  const [textFieldValue, setTextFieldValue] = useState("");
  const [textAreaValue, setTextAreaValue] = useState("");
  const [menuActive, setMenuActive] = useState<"karriere" | "persoenlichkeit">("karriere");
  const [filterValue, setFilterValue] = useState<"alle" | "video" | "podcast">("alle");

  return (
    <>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.colours", defaultMessage: "Colours & Tokens" })}>
        <div>
          <div className="mb-2 text-[11px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.colours.anchors"
              defaultMessage="Anchors – four roles, three user-changeable (page · surface · accent), ink fixed"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            <Swatch cls="bg-page" label="bg-page · Hintergrund" /> {/* i18n-allow */}
            <Swatch cls="bg-surface" label="bg-surface · Boxen" /> {/* i18n-allow */}
            <Swatch cls="bg-ink" label="bg-ink · Text · Signal" /> {/* i18n-allow */}
            <Swatch cls="bg-accent" label="bg-accent · Signal" /> {/* i18n-allow */}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[11px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.colours.danger"
              defaultMessage="Danger – fixed destructive button colour (never a highlight, never user-themable, never for emphasis)"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            <Swatch cls="bg-danger" label="bg-danger · Destruktiv" /> {/* i18n-allow */}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[11px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.colours.overlays"
              defaultMessage="Overlay backgrounds – four canonical rungs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            <Swatch cls="bg-ink/[0.02]" label="/[0.02] · empty wash" /> {/* i18n-allow */}
            <Swatch cls="bg-ink/[0.06]" label="/[0.06] · chip rest" /> {/* i18n-allow */}
            <Swatch cls="bg-ink/10" label="/10 · chip hover" /> {/* i18n-allow */}
            <Swatch cls="bg-ink/40" label="/40 · modal scrim" /> {/* i18n-allow */}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[11px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.colours.borders"
              defaultMessage="Borders & rings – four canonical rungs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            <RingSpecimen cls="ring-ink/5" label="ring-ink/5 · .box edge" /> {/* i18n-allow */}
            <RingSpecimen cls="ring-ink/10" label="ring-ink/10 · input/popover" /> {/* i18n-allow */}
            <RingSpecimen cls="ring-ink/15" label="ring-ink/15 · emphasis" /> {/* i18n-allow */}
            <RingSpecimen cls="ring-ink/30" label="ring-ink/30 · focus" /> {/* i18n-allow */}
          </div>
        </div>
        <div>
          <div className="mb-2 text-[11px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.colours.textWeight"
              defaultMessage="Text weight – seven canonical rungs"
            />
          </div>
          <div className="flex flex-wrap items-baseline gap-4 text-sm">
            <span className="text-ink">text-ink{/* i18n-allow */}</span>
            <span className="text-ink/80">/80{/* i18n-allow */}</span>
            <span className="text-ink/70">/70{/* i18n-allow */}</span>
            <span className="text-ink/55">/55{/* i18n-allow */}</span>
            <span className="text-ink/45">/45{/* i18n-allow */}</span>
            <span className="text-ink/40">/40{/* i18n-allow */}</span>
            <span className="text-ink/30">/30{/* i18n-allow */}</span>
            <span className="text-accent">text-accent{/* i18n-allow */}</span>
          </div>
        </div>
        <code className="font-mono text-[10px] text-ink/45">
          Text on fill: text-on-ink (bg-ink) · text-on-accent (bg-accent) · text-on-surface{/* i18n-allow */}
        </code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.hints", defaultMessage: "Hints" })}>
        <Specimen
          code='<div className="smycles-hint">'
          purpose={intl.formatMessage({
            id: "settings.styleguide.hintPurpose",
            defaultMessage: "Short, non-error guidance in the normal flow. Use for context, not failures or destructive warnings.",
          })}
        >
          <div className="smycles-hint w-full">
            <div className="text-[10px] tracking-[0.2em] text-accent/75">
              <FormattedMessage id="settings.styleguide.hintLabel" defaultMessage="Hint" />
            </div>
            <div className="mt-1 field-value text-ink">
              <FormattedMessage id="settings.styleguide.hintExample" defaultMessage="This explains an important consequence before you continue." />
            </div>
          </div>
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.loading", defaultMessage: "Loading" })}>
        <Specimen
          code='<div className="smycles-skeleton h-3 w-40 rounded" /> · aspect-[4/3] · rounded-full'
          purpose={intl.formatMessage({
            id: "settings.styleguide.skeletonPurpose",
            defaultMessage:
              "Breathing placeholder for content still in flight. The class carries the surface and the breath; width, height and radius stay at the call site, so the placeholder takes the shape of what it stands in for. Mirror the resting state of the real component, and show it only while nothing is displayable yet.",
          })}
        >
          <div className="flex items-center gap-3" aria-hidden="true">
            <div className="smycles-skeleton h-3 w-40 rounded" />
            <div className="smycles-skeleton aspect-[4/3] w-16" />
            <div className="smycles-skeleton h-8 w-24 rounded-full" />
          </div>
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.buttons", defaultMessage: "Buttons" })}>
        <Specimen
          code='<Button variant="primary">'
          purpose="Affirmative side of any choice — Submit / Confirm / Create / Add / Continue. Default when no variant is passed."
        >
          <Button><FormattedMessage id="common.save" defaultMessage="Save" /></Button>
        </Specimen>
        <Specimen
          code='<Button variant="secondary">'
          purpose="Quiet outlined at rest (15% ring, 70% text), FULLY INVERTS on hover. Dismiss / cancel; low-emphasis CTAs that should still feel deliberate on click."
        >
          <Button variant="secondary"><FormattedMessage id="common.cancel" defaultMessage="Cancel" /></Button>
        </Specimen>
        <Specimen
          code='<Button variant="destructive">'
          purpose="Red confirm side of a destructive action. @adr SPARK-0011-destructive-actions-require-confirmation@1: destructive never mutates on first click — always behind a second-step confirm."
        >
          <Button variant="destructive"><FormattedMessage id="common.delete" defaultMessage="Delete" /></Button>
        </Specimen>
        <Specimen
          code='<Button variant="toggle" aria-pressed={...}>'
          purpose="Sticky two-state pill where hover previews the NEXT click. Use only when the visual flip is the affordance (e.g. enter/leave a mode). If a label change carries state, use PRIMARY instead."
        >
          <Button
            variant="toggle"
            aria-pressed={toggleOn}
            onClick={() => setToggleOn((v) => !v)}
          >
            {toggleOn
              ? intl.formatMessage({ id: "settings.styleguide.demo.hideVisibility", defaultMessage: "Hide visibility" })
              : intl.formatMessage({ id: "settings.styleguide.demo.editVisibility", defaultMessage: "Edit visibility" })}
          </Button>
        </Specimen>
        <Specimen
          code='<Button variant="chip" size="sm">'
          purpose="Same geometry as SECONDARY but hover is a SOFT 6% wash (no invert). For browse / explore clicks — open picker, add row, expand section — that shouldn't read as 'you're confirming something'."
        >
          <Button variant="chip" size="sm">
            <FormattedMessage id="settings.styleguide.demo.addFilter" defaultMessage="+ Add filter" />
          </Button>
        </Specimen>
        <Specimen
          code='<Button variant="ghost">'
          purpose="The quietest action — pure text with hover-darken. No ring, no fill. Use when an action should be present but must not visually compete with surrounding content."
        >
          <Button variant="ghost"><FormattedMessage id="common.edit" defaultMessage="Edit" /></Button>
        </Specimen>
        <Specimen
          code='<Button variant="icon">'
          purpose="Icon-only with a guaranteed square hit area + gentle chip-wash hover. Settings cogs, row Download / Open, dismiss / delete close-X actions."
        >
          <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}>✕{/* i18n-allow */}</Button>
          <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "settings.styleguide.demo.download", defaultMessage: "Download" })}>↓{/* i18n-allow */}</Button>
          <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "settings.styleguide.demo.open", defaultMessage: "Open" })}>→{/* i18n-allow */}</Button>
        </Specimen>
        <Specimen
          code='<BackButton />'
          purpose="The universal '←' page-level nav-up affordance. Always calls navigate(-1), never a hard-coded route. Fixed shape, fixed size. Place in a mb-5 wrapper at the start of a sub-page."
        >
          <BackButton />
        </Specimen>
        <Specimen
          code='<Link className={buttonClasses({ variant: "primary" })} to="…">'
          purpose="How to make a React Router Link look like a Button. Used for in-app navigation that should read as a button (e.g. SharePage.tsx:265 'Open ↗'). preventDefault'd here so the demo doesn't navigate away from the Styleguide."
        >
          <Link
            to="/settings"
            onClick={(e) => e.preventDefault()}
            className={buttonClasses({ variant: "primary" })}
          >
            <FormattedMessage id="settings.styleguide.demo.toSettings" defaultMessage="To settings" />
          </Link>
        </Specimen>
        <Specimen
          code='size="sm" / size="compact" / size="md" / size="lg" / size="handle"'
          purpose="The five size scales. sm: row trailing actions, bulk-action bars. compact: admin '+ Hinzufügen', form panel buttons. md: default (SaveBar, modal confirms). lg: hero CTAs. handle: 48px circular slider/threshold handles only (ResultPanel A/B/C thresholds)."
        >
          <Button size="sm">sm{/* i18n-allow */}</Button>
          <Button size="compact">compact{/* i18n-allow */}</Button>
          <Button>md{/* i18n-allow */}</Button>
          <Button size="lg">lg{/* i18n-allow */}</Button>
          <Button size="handle">A{/* i18n-allow */}</Button>
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.pills", defaultMessage: "Pills & Segmented Control" })}>
        <Specimen
          code='<SegmentedControl value onChange options />'
          purpose="Pick exactly one of N options. There's no no-filter state — one option is always selected. Score-source switches, mode/lens pickers."
        >
          <SegmentedControl
            value={pill}
            onChange={setPill}
            options={[
              { value: "personen", label: intl.formatMessage({ id: "settings.styleguide.demo.people", defaultMessage: "People" }) },
              { value: "posts", label: intl.formatMessage({ id: "settings.styleguide.demo.posts", defaultMessage: "Posts" }) },
              { value: "projekte", label: intl.formatMessage({ id: "settings.styleguide.demo.projects", defaultMessage: "Projects" }) },
            ]}
          />
        </Specimen>
        <Specimen
          code='<FilterChip active onClick>'
          purpose='Ambient list-page filter with an explicit "Alle" no-filter default. Active state full-inverts. Used on every Grow page and Share Mine.'
        >
          <FilterChip active={filterValue === "alle"} onClick={() => setFilterValue("alle")}>
            <FormattedMessage id="settings.styleguide.demo.filterAll" defaultMessage="ALL" />
          </FilterChip>
          <FilterChip active={filterValue === "video"} onClick={() => setFilterValue("video")}>
            <FormattedMessage id="settings.styleguide.demo.filterVideo" defaultMessage="VIDEO" />
          </FilterChip>
          <FilterChip active={filterValue === "podcast"} onClick={() => setFilterValue("podcast")}>
            <FormattedMessage id="settings.styleguide.demo.filterPodcast" defaultMessage="PODCAST" />
          </FilterChip>
        </Specimen>
        <Specimen
          code='<KpiBadge tone active onClick info>'
          purpose="Compact label+value pill summarising a list row (match ranking: Smycles Score, Muss, Kann, Power-Fit). active is the toggle state and overrides tone with the accent fill; info appends an InfoTooltip as a sibling button, never nested in the label button. No danger tone: a failed gate is a state, and red is reserved for destructive actions."
        >
          <KpiBadge active={kpiTab === "score"} onClick={() => setKpiTab("score")}>
            <span>Smycles Score{/* i18n-allow */}</span>
            <span>82 %{/* i18n-allow */}</span>
          </KpiBadge>
          <KpiBadge
            active={kpiTab === "must"}
            onClick={() => setKpiTab("must")}
            info={{
              title: "Muss-Kriterien" /* i18n-allow */,
              ariaLabel: "Muss-Kriterien anzeigen" /* i18n-allow */,
              content: "Muss-Kriterien sind harte Gates: jedes muss erfüllt sein." /* i18n-allow */,
            }}
          >
            <span>Muss{/* i18n-allow */}</span>
            <span>1/2{/* i18n-allow */}</span>
          </KpiBadge>
          <KpiBadge active={kpiTab === "kann"} onClick={() => setKpiTab("kann")}>
            <span>Kann{/* i18n-allow */}</span>
            <span>2,4/3{/* i18n-allow */}</span>
          </KpiBadge>
          <KpiBadge tone="muted">
            <span>Power-Fit{/* i18n-allow */}</span>
            <span>–{/* i18n-allow */}</span>
          </KpiBadge>
        </Specimen>
        <Specimen code='<TriStateControl value={boolean | null} labels={{all, yes, no}} />'>
          <TriStateControl
            value={tri}
            onChange={setTri}
            labels={{
              all: intl.formatMessage({ id: "settings.styleguide.demo.triAny", defaultMessage: "Any" }),
              yes: intl.formatMessage({ id: "settings.styleguide.demo.triYes", defaultMessage: "Yes" }),
              no: intl.formatMessage({ id: "settings.styleguide.demo.triNo", defaultMessage: "No" }),
            }}
          />
        </Specimen>
        <Specimen code='<Switch checked={…} onChange={…} ariaLabel="…" />'>
          <Switch
            checked={switchBare}
            onChange={setSwitchBare}
            ariaLabel={intl.formatMessage({ id: "settings.styleguide.demo.switchAria", defaultMessage: "Switch demo" })}
          />
        </Specimen>
        <Specimen code='<Switch label={{ on: "Teilen", off: "Privat" }} … />'>
          <Switch
            checked={switchLabeled}
            onChange={setSwitchLabeled}
            ariaLabel={
              switchLabeled
                ? intl.formatMessage({ id: "settings.styleguide.demo.shareActive", defaultMessage: "Sharing active" })
                : intl.formatMessage({ id: "settings.styleguide.demo.shareInactive", defaultMessage: "Not sharing" })
            }
            label={{
              on: intl.formatMessage({ id: "settings.styleguide.demo.shareOn", defaultMessage: "Share" }),
              off: intl.formatMessage({ id: "settings.styleguide.demo.shareOff", defaultMessage: "Private" }),
            }}
          />
        </Specimen>
        <Specimen code='<NumberStepper value={…} onChange={…} min={1} />'>
          <NumberStepper
            value={stepperValue}
            onChange={setStepperValue}
            min={1}
            ariaLabel={intl.formatMessage({ id: "settings.styleguide.demo.stepperAria", defaultMessage: "Demo stepper" })}
          />
        </Specimen>
        <Specimen code='<TextInput value={…} onChange={…} aria-label="…" />'>
          <div className="w-full max-w-xs">
            <TextInput
              value={textFieldValue}
              onChange={(event) => setTextFieldValue(event.target.value)}
              aria-label={intl.formatMessage({ id: "settings.styleguide.demo.textFieldAria", defaultMessage: "Text field demo" })}
              placeholder={intl.formatMessage({ id: "settings.styleguide.demo.textFieldPlaceholder", defaultMessage: "Single line" })}
            />
          </div>
        </Specimen>
        <Specimen code='<TextArea mono rows={4} value={…} onChange={…} aria-label="…" />'>
          <div className="w-full max-w-md">
            <TextArea
              mono
              rows={4}
              value={textAreaValue}
              onChange={(event) => setTextAreaValue(event.target.value)}
              aria-label={intl.formatMessage({ id: "settings.styleguide.demo.textAreaAria", defaultMessage: "Text area demo" })}
            />
          </div>
        </Specimen>
        <Specimen code='<TextInput disabled readOnly value="…" />'>
          <div className="w-full max-w-xs">
            <TextInput
              disabled
              readOnly
              value={intl.formatMessage({ id: "settings.styleguide.demo.textFieldReadOnly", defaultMessage: "Read-only value" })}
              aria-label={intl.formatMessage({ id: "settings.styleguide.demo.textFieldReadOnlyAria", defaultMessage: "Read-only text field demo" })}
            />
          </div>
        </Specimen>
        <Specimen code='<SecretInput value={…} onChange={…} ariaLabel="…" />'>
          <div className="max-w-xs">
            <SecretInput
              value={secret}
              onChange={setSecret}
              ariaLabel={intl.formatMessage({ id: "settings.styleguide.demo.secretAria", defaultMessage: "Secret demo" })}
              placeholder="sk-…"
            />
          </div>
        </Specimen>
        <Specimen code='<PlusButton label={…} onClick={…} />'>
          <div className="flex flex-wrap items-center gap-4">
            <PlusButton label={intl.formatMessage({ id: "settings.styleguide.demo.addEntry", defaultMessage: "Add entry" })} onClick={() => {}} />
            <PlusButton label={intl.formatMessage({ id: "settings.styleguide.demo.skill", defaultMessage: "Skill" })} onClick={() => {}} />
            <PlusButton label={intl.formatMessage({ id: "settings.styleguide.demo.jobTitle", defaultMessage: "Job title" })} onClick={() => {}} />
            <PlusButton label={intl.formatMessage({ id: "settings.styleguide.demo.disabled", defaultMessage: "Disabled" })} onClick={() => {}} disabled />
          </div>
        </Specimen>
        <Specimen code='<AddPlaceholder label={…} onClick={…} />'>
          <AddPlaceholder label={intl.formatMessage({ id: "settings.styleguide.demo.newFolder", defaultMessage: "New folder" })} onClick={() => {}} />
        </Specimen>
        <Specimen code='<AddPlaceholder fullWidth label={…} onClick={…} />'>
          <div className="w-80">
            <AddPlaceholder label={intl.formatMessage({ id: "settings.styleguide.demo.addExperience", defaultMessage: "Add work experience" })} onClick={() => {}} fullWidth />
          </div>
        </Specimen>
        <Specimen code='<AddForm variant="empty | row" onAdd={…} fields={…} />'>
          <div className="w-80">
            <AddForm
              variant="empty"
              label={intl.formatMessage({ id: "settings.styleguide.demo.addEntry", defaultMessage: "Add entry" })}
              fields={[
                { key: "name", label: intl.formatMessage({ id: "settings.styleguide.demo.fieldName", defaultMessage: "Name" }), type: "text", required: true },
                { key: "note", label: intl.formatMessage({ id: "settings.styleguide.demo.fieldNote", defaultMessage: "Note" }), type: "text" },
              ]}
              onAdd={(payload) => console.log("[Styleguide] AddForm", payload)}
            />
          </div>
        </Specimen>
        <Specimen code='<MenuItem icon={…} trailing={…} active={…}>…</MenuItem>'>
          <div className="w-60 rounded-md bg-page p-1 ring-1 ring-ink/10">
            <MenuItem icon={<span>⚙{/* i18n-allow */}</span>}>
              <FormattedMessage id="settings.styleguide.demo.settings" defaultMessage="Settings" />
            </MenuItem>
            <MenuItem
              active={menuActive === "karriere"}
              onClick={() => setMenuActive("karriere")}
              trailing={<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
            >
              <FormattedMessage id="settings.styleguide.demo.careerQuestionnaire" defaultMessage="Career questionnaire" />
            </MenuItem>
            <MenuItem
              active={menuActive === "persoenlichkeit"}
              onClick={() => setMenuActive("persoenlichkeit")}
              trailing={<span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
            >
              <FormattedMessage id="settings.styleguide.demo.personality" defaultMessage="Personality" />
            </MenuItem>
            <MenuItem>
              <FormattedMessage id="settings.styleguide.demo.pinWithoutGroup" defaultMessage="+ Pin without a group" />
            </MenuItem>
          </div>
        </Specimen>
        <Specimen code="Tag-Pill (Item-Typ): bg-accent text-on-accent">
          <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-accent">
            <FormattedMessage id="settings.styleguide.demo.tagProject" defaultMessage="Project" />
          </span>
          <span className="inline-block rounded bg-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-accent">
            <FormattedMessage id="settings.styleguide.demo.tagPost" defaultMessage="Post" />
          </span>
        </Specimen>
        <Specimen code=".smycles-mandatory (Pflichtfeld-Badge)">
          <span>
            <FormattedMessage id="settings.styleguide.demo.fieldTitle" defaultMessage="Title" />
            <span className="smycles-mandatory">
              <FormattedMessage id="settings.styleguide.demo.mandatory" defaultMessage="Required" />
            </span>
          </span>
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.boxes", defaultMessage: "Boxes & Surfaces" })}>
        <div className="grid grid-cols-1 gap-3 @sm:grid-cols-3">
          <div className="flex flex-col gap-2">
            <div className="box bg-surface p-4 text-[12px] text-ink/70">
              <FormattedMessage id="settings.styleguide.boxes.box" defaultMessage=".box (panel, flat + ring)" />
            </div>
            <code className="font-mono text-[10px] text-ink/45">.box{/* i18n-allow */}</code>
          </div>
          <div className="flex flex-col gap-2">
            <div className="box bg-surface p-4">
              <div className="box-flat bg-page p-3 text-[12px] text-ink/70">
                <FormattedMessage id="settings.styleguide.boxes.boxFlat" defaultMessage=".box-flat (nested)" />
              </div>
            </div>
            <code className="font-mono text-[10px] text-ink/45">.box-flat{/* i18n-allow */}</code>
          </div>
          <div className="flex flex-col gap-2">
            <button type="button" className="box-button p-4 text-left text-[12px] text-ink/70">
              <FormattedMessage id="settings.styleguide.boxes.boxButton" defaultMessage=".box-button (slight lift)" />
            </button>
            <code className="font-mono text-[10px] text-ink/45">.box-button{/* i18n-allow */}</code>
          </div>
        </div>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.avatar", defaultMessage: "Avatar placeholder" })}>
        <Specimen code="Avatar (fallback = gold gradient + initials), size sm / md / lg">
          <Avatar person={{ name: "Greta Nowak" }} size="sm" />
          <Avatar person={{ name: "Greta Nowak" }} size="md" />
          <Avatar person={{ name: "Greta Nowak" }} size="lg" />
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.toast", defaultMessage: "Toast / Snackbar" })}>
        <Specimen code=".smycles-toast (signal colour) — buttons adapt via context tokens">
          <div className="smycles-toast pr-2">
            <span className="min-w-0 flex-1 text-[12px]">
              <FormattedMessage id="common.unsavedChanges" defaultMessage="Unsaved changes" />
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="secondary" size="sm"><FormattedMessage id="common.discard" defaultMessage="Discard" /></Button>
              <Button size="sm"><FormattedMessage id="common.save" defaultMessage="Save" /></Button>
            </div>
          </div>
        </Specimen>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.select", defaultMessage: "Select" })}>
        <div className="text-[11px] text-ink/55">
          <FormattedMessage
            id="settings.styleguide.select.ruleOfThumb"
            defaultMessage="<b>Rule of thumb:</b> <c>box</c> when the Select stands alone (a hero control in a panel). <c>plain</c> when there are sibling fields that share the affordance pattern (e.g. a profile detail grid)."
            values={{
              b: (chunks) => <span className="font-medium text-ink">{chunks}</span>,
              c: (chunks) => <code className="font-mono">{chunks}</code>,
            }}
          />
        </div>
        <Specimen code='variant="box" — white field with ring; standalone hero control'>
          <div className="w-full max-w-xs">
            <Select
              value="b"
              onChange={() => {}}
              options={[
                { value: "a", label: intl.formatMessage({ id: "settings.styleguide.demo.orgConstruction", defaultMessage: "Construction company" }) },
                { value: "b", label: intl.formatMessage({ id: "settings.styleguide.demo.orgFilm", defaultMessage: "Film production" }) },
                { value: "c", label: intl.formatMessage({ id: "settings.styleguide.demo.orgAgency", defaultMessage: "Agency" }) },
              ]}
            />
          </div>
        </Specimen>
        <Specimen code='variant="plain" — transparent with custom chevron; inside a field grid'>
          <div className="bg-page box-flat w-full max-w-xs px-3 py-3">
            <Select
              variant="plain"
              value="b"
              onChange={() => {}}
              options={[
                { value: "a", label: intl.formatMessage({ id: "settings.styleguide.demo.lensExplore", defaultMessage: "Explore" }) },
                { value: "b", label: intl.formatMessage({ id: "settings.styleguide.demo.lensBuild", defaultMessage: "Build · changed" }) },
                { value: "c", label: intl.formatMessage({ id: "settings.styleguide.demo.lensMaintain", defaultMessage: "Maintain" }) },
              ]}
            />
          </div>
        </Specimen>
        <code className="font-mono text-[10px] text-ink/45">&lt;Select /&gt; — components/design/Select.tsx{/* i18n-allow */}</code>
        <Specimen code="&lt;SearchableSelect /&gt; — long catalogues with optional filtering">
          <div className="w-full max-w-xs">
            <SearchableSelect
              value="b"
              onChange={() => {}}
              options={[
                { value: "a", label: "Abitur" },
                { value: "b", label: "Bachelor of Science" },
                { value: "c", label: "Master of Arts" },
              ]}
              placeholder="Choose degree" /* i18n-allow */
              searchPlaceholder="Type to filter …" /* i18n-allow */
              searchAriaLabel="Search catalogue" /* i18n-allow */
              noResultsLabel="No matches" /* i18n-allow */
              emptyOptionLabel="No degree selected" /* i18n-allow */
            />
          </div>
        </Specimen>
        <code className="font-mono text-[10px] text-ink/45">&lt;SearchableSelect /&gt; — components/design/SearchableSelect.tsx{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.disclosure", defaultMessage: "Disclosure" })}>
        <Specimen code="<Disclosure title count defaultOpen> — controlled via open/onToggle (accordion)">
          <div className="w-full max-w-sm">
            <Disclosure title="Resilience" count={4} defaultOpen> {/* i18n-allow */}
              <div className="text-[12px] text-ink/55">
                <FormattedMessage
                  id="settings.styleguide.disclosure.body"
                  defaultMessage="Cream box with a ›-chevron. A click rotates the chevron 90° and opens the panel below, separated by a hairline."
                />
              </div>
            </Disclosure>
          </div>
        </Specimen>
        <code className="font-mono text-[10px] text-ink/45">&lt;Disclosure /&gt; — components/design/Disclosure.tsx{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.pager", defaultMessage: "Pager" })}>
        <Specimen
          code="<Pager page totalPages onChange> — zero-based page"
          purpose="Previous / position / Next for lists paged in the client. Renders nothing for a single page, so the callsite carries no condition."
        >
          <PagerSpecimen />
        </Specimen>
        <code className="font-mono text-[10px] text-ink/45">&lt;Pager /&gt; — components/design/Pager.tsx{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.tooltip", defaultMessage: "Tooltip & InfoTooltip" })}>
        <Specimen
          code="<Tooltip content placement>"
          purpose="The shared hover/focus bubble: dark ink surface, arrow, CSS-only, aria-describedby. Wraps any trigger. interactive keeps the open bubble hoverable for scrollable/selectable content."
        >
          <Tooltip
            content={
              <FormattedMessage
                id="settings.styleguide.tooltip.body"
                defaultMessage="Appears on hover and keyboard focus."
              />
            }
          >
            <span className="text-[11px] text-ink/70 underline decoration-dotted">hover me{/* i18n-allow */}</span>
          </Tooltip>
        </Specimen>
        <Specimen
          code='<InfoTooltip ariaLabel title placement>'
          purpose="The standard ⓘ affordance for contextual explanations: glyph trigger + optional bold title line + body. Always interactive. SkillLevelInfoIcon is the skill-level wrapper. Used for level descriptions, score breakdowns, analysis notes."
        >
          <InfoTooltip
            ariaLabel={intl.formatMessage({ id: "settings.styleguide.infoTooltip.aria", defaultMessage: "Show explanation" })}
            title={<FormattedMessage id="settings.styleguide.infoTooltip.title" defaultMessage="Title line" />}
            placement="bottom"
          >
            <FormattedMessage
              id="settings.styleguide.infoTooltip.body"
              defaultMessage="Body text explaining the value next to the icon."
            />
          </InfoTooltip>
        </Specimen>
        <code className="font-mono text-[10px] text-ink/45">&lt;InfoTooltip /&gt; — components/design/InfoTooltip.tsx{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.arrows", defaultMessage: "Arrow glyphs" })}>
        <Specimen code="↗ leaves the page (route / external / share)">
          <button type="button" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-ink/45 transition hover:text-accent">
            <FormattedMessage id="settings.styleguide.demo.viewAll" defaultMessage="View all" /> <span aria-hidden="true">↗</span>
          </button>
        </Specimen>
        <Specimen code="→ stays on the page (a step forward in the flow)">
          <Button>
            <FormattedMessage id="settings.styleguide.demo.continue" defaultMessage="Continue →" />
          </Button>
        </Specimen>
        <Specimen code="› expand / disclosure / carousel-next">
          <button type="button" className="text-[12px] text-ink/70 transition hover:text-accent">
            <FormattedMessage id="settings.styleguide.demo.nextChevron" defaultMessage="Next ›" />
          </button>
        </Specimen>
        <div className="text-[12px] text-ink/55">
          <FormattedMessage
            id="settings.styleguide.arrows.note"
            defaultMessage="Three glyphs, three meanings, never mix them (see UI_CONVENTIONS · arrow glyphs)."
          />
        </div>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.boxHeader", defaultMessage: "BoxHeader" })}>
        <div className="box bg-surface p-5">
          <BoxHeader title={intl.formatMessage({ id: "settings.styleguide.demo.details", defaultMessage: "Details" })} right={<span className="text-[12px] text-ink/55">12{/* i18n-allow */}</span>} />
          <div className="mt-2 text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.boxHeader.note"
              defaultMessage="Title on the left, optional slot on the right (counter, toggle, link)."
            />
          </div>
        </div>
        <code className="font-mono text-[10px] text-ink/45">&lt;BoxHeader title right /&gt;{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.hero", defaultMessage: "Hero (pillar pages)" })}>
        <Hero
          title="MATCH"
          subtitle={
            <FormattedMessage
              id="settings.styleguide.hero.subtitle"
              defaultMessage="From the idea of a team{br}to the best team."
              values={{ br: <br /> }}
            />
          }
          body={intl.formatMessage({
            id: "settings.styleguide.hero.body",
            defaultMessage: "One source for all pillar landings: <Hero title subtitle body image cta />.",
          })}
          image={{ src: "/heroes/match.jpg" }}
          cta={{ kind: "button", label: intl.formatMessage({ id: "settings.styleguide.demo.exampleCta", defaultMessage: "Example CTA ↗" }), onClick: () => {} }}
        />
        <code className="font-mono text-[10px] text-ink/45">&lt;Hero /&gt; — components/layout/Hero.tsx{/* i18n-allow */}</code>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.personCards", defaultMessage: "Person cards" })}>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2">
            <ResultCard
              title={"Greta Nowak" /* i18n-allow */}
              sub={intl.formatMessage({ id: "settings.styleguide.demo.personGretaSub", defaultMessage: "Scenographer · Cologne" })}
              avatar={null}
              actions={<Button variant="secondary" size="sm"><FormattedMessage id="settings.styleguide.demo.pin" defaultMessage="+ Pin" /></Button>}
              onOpen={() => {}}
            />
            <ResultCard
              title={"Jonas Hardt" /* i18n-allow */}
              sub={intl.formatMessage({ id: "settings.styleguide.demo.personJonasSub", defaultMessage: "Lighting designer · Essen" })}
              avatar={null}
              embedded
              onOpen={() => {}}
            />
          </div>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.personCards.resultNote"
              defaultMessage="A list row in search and the search overlay; the embedded variant sits on page (box-flat)."
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;ResultCard /&gt; — components/search/ResultCard.tsx{/* i18n-allow */}</code>
        </div>
        <div className="flex flex-col gap-2">
          <div className="max-w-xs">
            <SavePersonCollectionCard
              id="styleguide-person"
              title={"Greta Nowak" /* i18n-allow */}
              subtitle={intl.formatMessage({ id: "settings.styleguide.demo.personGretaRole", defaultMessage: "Scenographer" })}
              onRemove={() => {}}
              removeLabel={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
              actions={<Button variant="secondary" size="sm"><FormattedMessage id="settings.styleguide.demo.community" defaultMessage="+ Community" /></Button>}
            />
          </div>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.personCards.collectionNote"
              defaultMessage="A collection tile for contacts, pins, and communities."
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;SavePersonCollectionCard /&gt; — components/save/SaveCollectionTemplate.tsx{/* i18n-allow */}</code>
        </div>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.sharedItemCards", defaultMessage: "SharedItem cards" })}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-72">
              <ShareItemCard item={FIXTURE_ITEM} showActivity />
            </div>
            <div className="w-72">
              <ShareItemCard item={FIXTURE_ITEM_NO_IMAGE} index={1} />
            </div>
          </div>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.sharedItemCards.cardNote"
              defaultMessage="4:3 cover + stats; showActivity reveals the activity chips (Watching & Joined). Without a mainImage the cover falls back to the AbstractTile pattern."
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;ShareItemCard /&gt; — components/share/ShareItemCard.tsx{/* i18n-allow */}</code>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-start gap-4">
            <div className="w-80">
              <ShareItemCardFeed item={FIXTURE_ITEM} />
            </div>
            <div className="w-64">
              <ShareItemCardFeed item={FIXTURE_ITEM_NO_IMAGE} index={1} />
            </div>
          </div>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.sharedItemCards.feedNote"
              defaultMessage="Fixed 4:5 tile, 60% image / 40% text. Sizes in use: the Discover grid on the left (4 per row), the horizontal rows of the SHARE landing on the right."
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;ShareItemCardFeed /&gt; — components/share/ShareItemCard.tsx{/* i18n-allow */}</code>
        </div>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.horizontalCardCarousel", defaultMessage: "Horizontal card carousel" })}>
        <div className="flex flex-col gap-2">
          <HorizontalCardCarousel>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="w-64 shrink-0">
                <ShareItemCardCover item={i % 2 === 0 ? FIXTURE_ITEM : FIXTURE_ITEM_NO_IMAGE} index={i} />
              </div>
            ))}
          </HorizontalCardCarousel>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.horizontalCardCarousel.note"
              defaultMessage="Vertical wheel gestures become horizontal scroll; page dots derive from the row's rendered width and stay in sync with drag/trackpad scrolling and dot clicks. One shared measurement/pagination implementation for Home's own-items row and both SHARE landing rows — do not hand-roll a second scroll row."
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;HorizontalCardCarousel /&gt; — components/design/HorizontalCardCarousel.tsx{/* i18n-allow */}</code>
        </div>
      </GuideCard>

      <GuideCard title={intl.formatMessage({ id: "settings.styleguide.section.powerCards", defaultMessage: "Power cards" })}>
        <div className="flex flex-col gap-2">
          {/* The real call sites — same props, same look. The
              tile is square-ish and sized by its grid column, so the wrapper
              widths below mirror the typical column width of each context. */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-24">
              <PowerTile variant="score" abbr="Ada" name="Adaption" active={false} score={72} onSelect={() => {}} />
            </div>
            <div className="w-32">
              <PowerTile tag="Re" abbr="Ada" name="Adaption" active={false} score={72} badge imageSrc="/powers/ada.jpg" onSelect={() => {}} />
            </div>
            <div className="w-32">
              <PowerTile
                tag="Re"
                abbr="Ada"
                name="Adaption"
                active
                imageSrc="/powers/ada.jpg"
                weightLevel={2}
                onWeightLevelSelect={() => {}}
                weightLevelLabels={["Nicht relevant", "Niedrig", "Mittel", "Hoch"]}
                className="power-tile--white"
              />
            </div>
          </div>
          <div className="text-[12px] text-ink/55">
            <FormattedMessage
              id="settings.styleguide.powerCards.note"
              defaultMessage="Left to right, as in use: profile sidebar (score variant, 5-up grid) · Power Map with medal, hover artwork and measured level · Match weighting with <c>power-tile--white</c> (click cycles the four levels, artwork sharpness mirrors the level). The size always comes from the grid, never from the card."
              values={{ c: (chunks) => <code className="font-mono text-[10px]">{chunks}</code> }}
            />
          </div>
          <code className="font-mono text-[10px] text-ink/45">&lt;PowerTile /&gt; — components/design/PowerTile.tsx{/* i18n-allow */}</code>
        </div>
      </GuideCard>
    </>
  );
}
